/**
 * Specialized image processing utilities for try-on profile pictures
 * Handles resizing to exact dimensions (320x640) required for try-on functionality
 */

export interface TryOnImageProcessingResult {
  processedFile: File;
  originalSize: number;
  processedSize: number;
  originalDimensions: { width: number; height: number };
  processedDimensions: { width: number; height: number };
  compressionRatio: number;
}

export interface TryOnImageValidation {
  isValid: boolean;
  error?: string;
  warnings?: string[];
}

export class TryOnImageProcessor {
  // Try-on specific constants
  static readonly TARGET_WIDTH = 320;
  static readonly TARGET_HEIGHT = 640;
  static readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  static readonly MIN_WIDTH = 160;
  static readonly MIN_HEIGHT = 320;
  static readonly ALLOWED_FORMATS = ['image/jpeg', 'image/png', 'image/webp'];
  static readonly OUTPUT_QUALITY = 0.85;
  static readonly OUTPUT_FORMAT = 'jpeg';

  /**
   * Process an image for try-on use - resize to exact 320x640 dimensions
   * @param file Original image file
   * @returns Processed image ready for try-on
   */
  static async processForTryOn(file: File): Promise<TryOnImageProcessingResult> {
    console.log('[TryOnImageProcessor] Starting try-on image processing', {
      fileName: file.name,
      originalSize: file.size,
      targetDimensions: `${this.TARGET_WIDTH}x${this.TARGET_HEIGHT}`,
    });

    try {
      // Load the image
      const img = await this.loadImage(file);
      const originalDimensions = { width: img.width, height: img.height };

      // Create canvas with exact try-on dimensions
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      canvas.width = this.TARGET_WIDTH;
      canvas.height = this.TARGET_HEIGHT;

      // Calculate how to fit the image into the target dimensions
      const { sourceX, sourceY, sourceWidth, sourceHeight } = this.calculateCropArea(
        img.width,
        img.height,
        this.TARGET_WIDTH,
        this.TARGET_HEIGHT
      );

      // Clear canvas with white background (in case of transparency)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, this.TARGET_WIDTH, this.TARGET_HEIGHT);

      // Draw the image cropped and resized to exact dimensions
      ctx.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight, // Source rectangle
        0, 0, this.TARGET_WIDTH, this.TARGET_HEIGHT   // Destination rectangle
      );

      // Convert to blob with high quality
      const processedBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create processed image blob'));
            }
          },
          `image/${this.OUTPUT_FORMAT}`,
          this.OUTPUT_QUALITY
        );
      });

      // Create processed file
      const processedFile = new File(
        [processedBlob],
        this.generateTryOnFileName(file.name),
        {
          type: `image/${this.OUTPUT_FORMAT}`,
          lastModified: Date.now(),
        }
      );

      const result: TryOnImageProcessingResult = {
        processedFile,
        originalSize: file.size,
        processedSize: processedFile.size,
        originalDimensions,
        processedDimensions: { width: this.TARGET_WIDTH, height: this.TARGET_HEIGHT },
        compressionRatio: file.size / processedFile.size,
      };

      console.log('[TryOnImageProcessor] Processing completed', {
        originalSize: result.originalSize,
        processedSize: result.processedSize,
        compressionRatio: result.compressionRatio.toFixed(2),
        originalDimensions: result.originalDimensions,
        processedDimensions: result.processedDimensions,
      });

      return result;
    } catch (error) {
      console.error('[TryOnImageProcessor] Processing failed', error);
      throw new Error(`Try-on image processing failed: ${error}`);
    }
  }

  /**
   * Validate image for try-on processing
   * @param file Image file to validate
   * @returns Validation result with any warnings
   */
  static async validateForTryOn(file: File): Promise<TryOnImageValidation> {
    const warnings: string[] = [];

    // Check file type
    if (!file.type.startsWith('image/')) {
      return { isValid: false, error: 'File must be an image' };
    }

    // Check supported formats
    if (!this.ALLOWED_FORMATS.includes(file.type)) {
      return { 
        isValid: false, 
        error: `Unsupported image format. Please use JPEG, PNG, or WebP` 
      };
    }

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      return { 
        isValid: false, 
        error: `Image file is too large. Maximum size is ${this.MAX_FILE_SIZE / (1024 * 1024)}MB` 
      };
    }

    // Check image dimensions
    try {
      const img = await this.loadImage(file);
      
      // Check minimum dimensions
      if (img.width < this.MIN_WIDTH || img.height < this.MIN_HEIGHT) {
        return { 
          isValid: false, 
          error: `Image is too small. Minimum size is ${this.MIN_WIDTH}x${this.MIN_HEIGHT} pixels` 
        };
      }

      // Check aspect ratio and provide warnings
      const aspectRatio = img.width / img.height;
      const targetAspectRatio = this.TARGET_WIDTH / this.TARGET_HEIGHT; // 0.5

      if (Math.abs(aspectRatio - targetAspectRatio) > 0.2) {
        if (aspectRatio > targetAspectRatio) {
          warnings.push('Image is wider than ideal - sides will be cropped for try-on');
        } else {
          warnings.push('Image is taller than ideal - top/bottom will be cropped for try-on');
        }
      }

      // Check if image is much larger than needed
      if (img.width > this.TARGET_WIDTH * 4 || img.height > this.TARGET_HEIGHT * 4) {
        warnings.push('Large image detected - will be optimized for faster processing');
      }

      // Check if image is portrait oriented (good for try-on)
      if (aspectRatio > 1) {
        warnings.push('Landscape images work best when they show a full-body view');
      }

      return { isValid: true, warnings };
    } catch (error) {
      return { isValid: false, error: 'Invalid or corrupted image file' };
    }
  }

  /**
   * Calculate the crop area to fit image into target dimensions while maintaining aspect ratio
   * Uses center cropping to focus on the most important part of the image
   */
  private static calculateCropArea(
    imgWidth: number,
    imgHeight: number,
    targetWidth: number,
    targetHeight: number
  ): { sourceX: number; sourceY: number; sourceWidth: number; sourceHeight: number } {
    const imgAspectRatio = imgWidth / imgHeight;
    const targetAspectRatio = targetWidth / targetHeight;

    let sourceWidth: number;
    let sourceHeight: number;
    let sourceX: number;
    let sourceY: number;

    if (imgAspectRatio > targetAspectRatio) {
      // Image is wider than target - crop horizontally
      sourceHeight = imgHeight;
      sourceWidth = imgHeight * targetAspectRatio;
      sourceX = (imgWidth - sourceWidth) / 2; // Center horizontally
      sourceY = 0;
    } else {
      // Image is taller than target - crop vertically
      sourceWidth = imgWidth;
      sourceHeight = imgWidth / targetAspectRatio;
      sourceX = 0;
      sourceY = (imgHeight - sourceHeight) / 4; // Crop more from bottom (focus on upper body)
    }

    return { sourceX, sourceY, sourceWidth, sourceHeight };
  }

  /**
   * Load image from file
   */
  private static loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image'));
      };
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Generate filename for processed try-on image
   */
  private static generateTryOnFileName(originalName: string): string {
    const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
    return `${nameWithoutExt}_tryon_${this.TARGET_WIDTH}x${this.TARGET_HEIGHT}.${this.OUTPUT_FORMAT}`;
  }

  /**
   * Create a preview of how the image will look after processing
   * @param file Original image file
   * @returns Data URL of the preview
   */
  static async createPreview(file: File): Promise<string> {
    const img = await this.loadImage(file);
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Create a smaller preview (160x320)
    const previewWidth = 160;
    const previewHeight = 320;
    
    canvas.width = previewWidth;
    canvas.height = previewHeight;

    const { sourceX, sourceY, sourceWidth, sourceHeight } = this.calculateCropArea(
      img.width,
      img.height,
      previewWidth,
      previewHeight
    );

    // White background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, previewWidth, previewHeight);

    // Draw preview
    ctx.drawImage(
      img,
      sourceX, sourceY, sourceWidth, sourceHeight,
      0, 0, previewWidth, previewHeight
    );

    return canvas.toDataURL('image/jpeg', 0.8);
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get processing info for user display
   */
  static getProcessingInfo(file: File): {
    willBeResized: boolean;
    willBeCompressed: boolean;
    estimatedOutputSize: string;
    processingDescription: string;
  } {
    const willBeResized = true; // Always resize to exact dimensions
    const willBeCompressed = file.size > 500 * 1024; // Compress if larger than 500KB
    
    // Estimate output size (typically 100-300KB for 320x640 JPEG)
    const estimatedBytes = Math.min(file.size * 0.3, 300 * 1024);
    const estimatedOutputSize = this.formatFileSize(estimatedBytes);

    let processingDescription = `Image will be resized to ${this.TARGET_WIDTH}×${this.TARGET_HEIGHT} pixels for optimal try-on results`;
    
    if (willBeCompressed) {
      processingDescription += ' and optimized for faster upload';
    }

    return {
      willBeResized,
      willBeCompressed,
      estimatedOutputSize,
      processingDescription,
    };
  }
}